import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';

import { useSubmitEnquiry } from '@/hooks/useSubmitEnquiry';
import {
  consultationFormDefaults,
  consultationFormSchema,
  type ConsultationFormValues,
} from '@/schemas/enquiry';

/**
 * The import-advice request's state and submission, with no layout attached.
 *
 * It is a hook rather than the top of the form component because the form is
 * two things that change for different reasons: what is asked, and what happens
 * when it is sent. The organism only arranges fields; every decision about the
 * request — what the payload looks like, which optional fields are omitted
 * rather than sent empty, where the enquiry says it came from — lives here.
 *
 * Shared rather than page-local. `/free-import-advice` and the band on
 * /contact send the same enquiry, and the submission is the half of a form
 * that must not be written twice: a second copy is a second chance for one
 * page to omit the honeypot, forget a `details` key, or send a `kind` the
 * inbox does not group.
 *
 * `pagePath` is read from the router rather than passed in, so the enquiry
 * records which page it was actually sent from without either caller being
 * trusted to say. That is the one thing that legitimately differs between the
 * two forms, and it is derived.
 *
 * ## Why `kind` still says `consultation`
 *
 * The page is called "Get Free Advice" because that is the offer a visitor
 * understands. The *service* is still "Import consultation" — one of the
 * Kabex named (§59), with its own page at `/services/import-consultation` — and
 * `consultation` is the value every enquiry in the database already carries.
 * Renaming the kind to match a button would split one lead type into two in
 * the inbox and orphan every row sent before today. The marketing name and the
 * domain name are allowed to differ; this comment is the seam.
 *
 * §32: this sends a REQUEST. The site has no calendar and invents no
 * availability (A16), which is why the form no longer asks for a date or a
 * time at all — `meeting_type` is the only scheduling fact it carries, and it
 * says where to send the answer rather than when to meet.
 */
export function useConsultationForm() {
  const { pathname } = useLocation();
  const submit = useSubmitEnquiry();
  const { control, register, handleSubmit } = useForm<ConsultationFormValues>({
    resolver: zodResolver(consultationFormSchema),
    defaultValues: consultationFormDefaults,
  });

  const onSubmit = handleSubmit((values) => {
    submit.mutate({
      kind: 'consultation',
      fullName: values.fullName,
      phone: values.phone,
      email: values.email,
      ...(values.message ? { message: values.message } : {}),
      details: { meeting_type: values.meetingType },
      pagePath: pathname,
      ...(values.companyWebsite ? { companyWebsite: values.companyWebsite } : {}),
    });
  });

  return {
    control,
    register,
    onSubmit,
    isPending: submit.isPending,
    error: submit.error,
    receipt: submit.data?.referenceNo ?? null,
  };
}

export type ConsultationFormState = ReturnType<typeof useConsultationForm>;
